import { METHOD_START_MESSAGE } from '../common/constants';
import SDKConnector from '../SDKConnector';
import { CALLING_BACKEND } from '../common/types';
import log from '../Logger';
import { getCallingBackEnd, uploadLogs } from '../common/Utils';
import { WxCallBackendConnector } from './WxCallBackendConnector';
import { BroadworksBackendConnector } from './BroadworksBackendConnector';
import { Eventing } from '../Events/impl';
import { UcmBackendConnector } from './UcmBackendConnector';
import { METRIC_EVENT, METRIC_TYPE, VOICEMAIL_ACTION } from '../Metrics/types';
import { getMetricManager } from '../Metrics';
import { VOICEMAIL_FILE, METHODS } from './constants';
export class Voicemail extends Eventing {
    logger;
    sdkConnector;
    webex;
    callingBackend;
    backendConnector;
    metricManager;
    constructor(webex, logger) {
        super();
        this.logger = logger;
        this.sdkConnector = SDKConnector;
        if (!this.sdkConnector.getWebex()) {
            SDKConnector.setWebex(webex);
        }
        this.webex = this.sdkConnector.getWebex();
        this.metricManager = getMetricManager(this.webex, undefined);
        this.callingBackend = getCallingBackEnd(this.webex);
        this.initializeBackendConnector();
        log.setLogger(logger.level, VOICEMAIL_FILE);
    }
    async init() {
        const loggerContext = {
            file: VOICEMAIL_FILE,
            method: METHODS.INIT,
        };
        try {
            log.info(METHOD_START_MESSAGE, loggerContext);
            const response = this.backendConnector.init();
            log.log('Voicemail connector initialized successfully', loggerContext);
            return response;
        }
        catch (err) {
            log.error(`Failed to initialize voicemail: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            throw err;
        }
    }
    initializeBackendConnector() {
        log.info(METHOD_START_MESSAGE, {
            file: VOICEMAIL_FILE,
            method: METHODS.INITIALIZE_BACKEND_CONNECTOR,
        });
        switch (this.callingBackend) {
            case CALLING_BACKEND.WXC: {
                this.backendConnector = new WxCallBackendConnector(this.webex, this.logger);
                break;
            }
            case CALLING_BACKEND.BWRKS: {
                this.backendConnector = new BroadworksBackendConnector(this.webex, this.logger);
                break;
            }
            case CALLING_BACKEND.UCM: {
                this.backendConnector = new UcmBackendConnector(this.webex, this.logger);
                break;
            }
            default: {
                throw new Error('Calling backend is not identified, exiting....');
            }
        }
    }
    submitMetric(response, metricAction, messageId) {
        const { statusCode, data: { error: errorMessage }, } = response;
        if (statusCode >= 200 && statusCode < 300) {
            this.metricManager.submitVoicemailMetric(METRIC_EVENT.VOICEMAIL, metricAction, METRIC_TYPE.BEHAVIORAL, messageId);
        }
        else {
            this.metricManager.submitVoicemailMetric(METRIC_EVENT.VOICEMAIL_ERROR, metricAction, METRIC_TYPE.BEHAVIORAL, messageId, errorMessage, statusCode);
        }
    }
    async getVoicemailList(offset, offsetLimit, sort, refresh) {
        const loggerContext = {
            file: VOICEMAIL_FILE,
            method: METHODS.GET_VOICEMAIL_LIST,
        };
        try {
            log.info(`${METHOD_START_MESSAGE} with: offset=${offset}, limit=${offsetLimit}, sort=${sort}, refresh=${refresh}`, loggerContext);
            const response = await this.backendConnector.getVoicemailList(offset, offsetLimit, sort, refresh);
            this.submitMetric(response, VOICEMAIL_ACTION.GET_VOICEMAILS);
            log.log(`Successfully retrieved voicemail list: statusCode=${response.statusCode}`, loggerContext);
            return response;
        }
        catch (err) {
            log.error(`Failed to get voicemail list: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            throw err;
        }
    }
    async getVoicemailContent(messageId) {
        const loggerContext = {
            file: VOICEMAIL_FILE,
            method: METHODS.GET_VOICEMAIL_CONTENT,
        };
        log.info(`${METHOD_START_MESSAGE} with: messageId=${messageId}`, loggerContext);
        const response = await this.backendConnector.getVoicemailContent(messageId);
        this.submitMetric(response, VOICEMAIL_ACTION.GET_VOICEMAIL_CONTENT, messageId);
        log.log(`Successfully retrieved voicemail content for messageId=${messageId}, statusCode=${response.statusCode}`, loggerContext);
        return response;
    }
    async getVoicemailSummary() {
        const loggerContext = {
            file: VOICEMAIL_FILE,
            method: METHODS.GET_VOICEMAIL_SUMMARY,
        };
        log.info(METHOD_START_MESSAGE, loggerContext);
        const response = await this.backendConnector.getVoicemailSummary();
        if (response !== null) {
            this.submitMetric(response, VOICEMAIL_ACTION.GET_VOICEMAIL_SUMMARY);
            log.log(`Successfully retrieved voicemail summary: statusCode=${response.statusCode}`, loggerContext);
        }
        return response;
    }
    async voicemailMarkAsRead(messageId) {
        const loggerContext = {
            file: VOICEMAIL_FILE,
            method: METHODS.VOICEMAIL_MARK_AS_READ,
        };
        log.info(`${METHOD_START_MESSAGE} with: messageId=${messageId}`, loggerContext);
        const response = await this.backendConnector.voicemailMarkAsRead(messageId);
        this.submitMetric(response, VOICEMAIL_ACTION.MARK_READ, messageId);
        log.log(`Successfully marked voicemail as read: messageId=${messageId}, statusCode=${response.statusCode}`, loggerContext);
        return response;
    }
    async voicemailMarkAsUnread(messageId) {
        const loggerContext = {
            file: VOICEMAIL_FILE,
            method: METHODS.VOICEMAIL_MARK_AS_UNREAD,
        };
        log.info(`${METHOD_START_MESSAGE} with: messageId=${messageId}`, loggerContext);
        const response = await this.backendConnector.voicemailMarkAsUnread(messageId);
        this.submitMetric(response, VOICEMAIL_ACTION.MARK_UNREAD, messageId);
        log.log(`Successfully marked voicemail as unread: messageId=${messageId}, statusCode=${response.statusCode}`, loggerContext);
        return response;
    }
    async deleteVoicemail(messageId) {
        const loggerContext = {
            file: VOICEMAIL_FILE,
            method: METHODS.DELETE_VOICEMAIL,
        };
        log.info(`${METHOD_START_MESSAGE} with: messageId=${messageId}`, loggerContext);
        const response = await this.backendConnector.deleteVoicemail(messageId);
        this.submitMetric(response, VOICEMAIL_ACTION.DELETE, messageId);
        log.log(`Successfully deleted voicemail: messageId=${messageId}, statusCode=${response.statusCode}`, loggerContext);
        return response;
    }
    async getVMTranscript(messageId) {
        const loggerContext = {
            file: VOICEMAIL_FILE,
            method: METHODS.GET_VM_TRANSCRIPT,
        };
        log.info(`${METHOD_START_MESSAGE} with: messageId=${messageId}`, loggerContext);
        const response = await this.backendConnector.getVMTranscript(messageId);
        if (response !== null) {
            this.submitMetric(response, VOICEMAIL_ACTION.TRANSCRIPT, messageId);
            log.log(`Successfully retrieved voicemail transcript: messageId=${messageId}, statusCode=${response.statusCode}`, loggerContext);
        }
        return response;
    }
    resolveContact(callingPartyInfo) {
        const loggerContext = {
            file: VOICEMAIL_FILE,
            method: METHODS.RESOLVE_CONTACT,
        };
        log.info(METHOD_START_MESSAGE, loggerContext);
        const response = this.backendConnector.resolveContact(callingPartyInfo);
        log.log('Contact resolution completed successfully', loggerContext);
        return response;
    }
    getSDKConnector() {
        return this.sdkConnector;
    }
}
export const createVoicemailClient = (webex, logger) => new Voicemail(webex, logger);
